'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowUp } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type TouchEvent, useRef } from 'react';

/** Movement below this many px counts as a tap (dismiss) rather than a swipe. */
const TAP_MAX_MOVE_PX = 10;
type IosSwipeFullscreenLayerProps = {
  onDismiss: () => void;
};

/**
 * Transparent layer over the player while Safari's toolbars are expanded. Swipes fall through to
 * native document scrolling (no `preventDefault`), which is what makes Safari collapse its toolbars.
 */
export default function IosSwipeFullscreenLayer(t0) {
  const $ = _c(18);
  const {
    onDismiss
  } = t0;
  const t = useTranslations("full3d-swipe-fullscreen");
  const touchStartRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = event => {
      const touch = event.touches[0];
      touchStartRef.current = touch ? {
        x: touch.clientX,
        y: touch.clientY
      } : null;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleTouchStart = t1;
  let t2;
  if ($[1] !== onDismiss) {
    t2 = event_0 => {
      const start = touchStartRef.current;
      const end = event_0.changedTouches[0];
      touchStartRef.current = null;
      if (!start || !end) {
        return;
      }
      if (Math.abs(end.clientX - start.x) < TAP_MAX_MOVE_PX && Math.abs(end.clientY - start.y) < TAP_MAX_MOVE_PX) {
        onDismiss();
      }
    };
    $[1] = onDismiss;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleTouchEnd = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArrowUp aria-hidden={true} className="size-7 motion-safe:animate-bounce" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("hint");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <span className="subheader-3">{t4}</span>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("tap-to-dismiss");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <span className="caption text-white/60">{t6}</span>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t7) {
    t8 = <div role="status" className="bg-vive-popup/70 pointer-events-none absolute top-1/2 left-1/2 flex w-max max-w-[calc(100%_-_40px)] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1 rounded-2xl px-5 py-3.5 text-center text-white backdrop-blur-xs">{t3}{t5}{t7}</div>;
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== handleTouchEnd || $[16] !== t8) {
    t9 = <div data-component="ios-swipe-fullscreen-layer" className="z-floating absolute inset-0 touch-pan-y" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>{t8}</div>;
    $[15] = handleTouchEnd;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}